// Proposal currency rules (fix/proposal-currency-override).
//
// The proposal owns its currency (quotes.currency_code). The recipient's
// country only SUGGESTS a default for a brand-new proposal; once the
// operator picks a currency, or the proposal already exists, that choice
// is never silently replaced. Changing currency relabels the amounts —
// prices are kept exactly as entered (no FX conversion).

// Used only if GET /quotes/currencies fails; mirrors the head of
// backend currency_utils.proposal_currency_options().
export const FALLBACK_PROPOSAL_CURRENCIES = ["USD", "CAD", "GBP", "EUR", "AUD"];

/**
 * Should a country-derived suggestion replace the current proposal currency?
 * Only on a NEW proposal whose currency the operator has not chosen.
 */
export function shouldApplySuggestedCurrency({ isEdit, overridden, suggested }) {
  return !isEdit && !overridden && !!suggested;
}

/** Relabel every line to the proposal currency without touching any amount. */
export function applyCurrencyToLines(lines, currencyCode) {
  return (lines || []).map(l => ({ ...l, line_currency_code: currencyCode }));
}

let displayNames = null;
try { displayNames = new Intl.DisplayNames(["en"], { type: "currency" }); } catch { displayNames = null; }

/** "CAD · Canadian Dollar" — falls back to the bare code. */
export function currencyLabel(code) {
  let name = "";
  try { name = displayNames ? displayNames.of(code) : ""; } catch { name = ""; }
  return name && name !== code ? `${code} · ${name}` : code;
}

/**
 * Selector options: the supported list, plus the proposal's current code
 * if it is somehow outside that list (so a legacy value is never hidden
 * or silently swapped by the <select>).
 */
export function currencyOptions(supported, current) {
  const codes = (supported && supported.length ? supported : FALLBACK_PROPOSAL_CURRENCIES).slice();
  if (current && !codes.includes(current)) codes.unshift(current);
  return codes.map(code => ({ code, label: currencyLabel(code) }));
}

/** The app's proposal money format: USD -> US$39,500.00, CAD -> CA$39,500.00. */
export function formatProposalMoney(n, c) {
  try { return new Intl.NumberFormat("en-GB", { style: "currency", currency: c || "USD" }).format(n ?? 0); }
  catch { return `${c} ${(Number(n) || 0).toFixed(2)}`; }
}
